import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Home, 
  Building, 
  Wrench, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Clock,
  DollarSign,
  FileText,
  Video
} from 'lucide-react';
import { mockServices } from '../data/mock';

const Services = () => {
  const iconMap = {
    Home: Home,
    Building: Building,
    Wrench: Wrench,
    Users: Users,
    FileText: FileText,
    Video: Video
  };

  const processSteps = [
    {
      step: "01",
      title: "Consulta Inicial Online",
      description: "Agendamos una videollamada para entender tu visión, medidas, necesidades y presupuesto.",
      icon: Users
    },
    {
      step: "02", 
      title: "Diseño Conceptual",
      description: "Creamos las primeras propuestas y bocetos digitales basados en tus requerimientos.",
      icon: Home
    },
    {
      step: "03",
      title: "Planos Técnicos", 
      description: "Elaboramos los planos 2D y 3D detallados con todas las especificaciones, listos para construir.",
      icon: Building
    },
    {
      step: "04",
      title: "Entrega Digital",
      description: "Recibes los archivos finales (PDF, AutoCAD, renders) por correo. Sin desplazamientos ni esperas.",
      icon: CheckCircle
    }
  ];

  return (
    <section id="servicios" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-6 bg-red-100 text-red-800 border-red-200 px-4 py-2">
            Nuestros Servicios
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Servicios
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-700"> 100% Online</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Diseño de planos y asesoramiento técnico totalmente remotos. Trabajamos contigo desde cualquier parte del mundo.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-20">
          {mockServices.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card 
                key={service.id} 
                className="border-0 shadow-xl bg-white hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group"
              >
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors duration-300">
                    <IconComponent size={32} className="text-red-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <CardTitle className="text-xl text-slate-900 group-hover:text-red-600 transition-colors duration-300">
                    {service.name}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <p className="text-slate-600 text-center leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-red-600 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-semibold text-red-600 text-lg">{service.price}</span>
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Clock size={14} />
                        <span>5-14 días</span>
                      </div>
                    </div>
                    
                    <Button
                      data-testid={`service-cta-${service.id}`}
                      onClick={() => document.getElementById('paquetes')?.scrollIntoView({ behavior: 'smooth' })}
                      className="w-full bg-red-600 hover:bg-red-700 text-white group-hover:scale-105 transition-all duration-300"
                    >
                      Ver Paquetes
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Process Section */}
        <div className="bg-gradient-to-br from-slate-50 to-red-50 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Nuestro Proceso de
              <span className="text-red-600"> Trabajo</span>
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un enfoque sistemático y profesional que garantiza resultados excepcionales en cada proyecto
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="text-center group">
                  {/* Step Number */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-all duration-300 group-hover:scale-110">
                      <span className="text-2xl font-bold text-red-600 group-hover:text-white transition-colors duration-300">
                        {step.step}
                      </span>
                    </div>
                    {index < processSteps.length - 1 && (
                      <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-red-200"></div>
                    )}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors duration-300">
                    <IconComponent size={24} className="text-red-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  
                  {/* Content */}
                  <h4 className="text-lg font-semibold text-slate-900 mb-3 group-hover:text-red-600 transition-colors duration-300">
                    {step.title}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-red-600 rounded-3xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              ¿Listo para Comenzar tu Proyecto Online?
            </h3>
            <p className="text-xl mb-8 text-red-100 max-w-2xl mx-auto">
              Elige un paquete y recibe tus planos profesionales sin moverte de casa.
            </p>
            <div className="flex justify-center">
              <Button 
                variant="outline"
                size="lg"
                data-testid="services-cta-packages"
                onClick={() => document.getElementById('paquetes')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold hover:scale-105 transition-all duration-300"
              >
                Ver Paquetes Online
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;